import { useContext, useMemo, useState } from "react"
import BasePage from "../../../components/layout/BasePage"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import { ModalContext } from "../../../components/contexts/ModalContext"
import OfferTableRow from "../components/offer/OfferTableRow"
import { useQuery } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { OfferGet } from "../../../data/Types"
import { formatCurrency, getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { P } from "../../../auth/permissions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { ReactComponent as PLUSINCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import AddOfferModal from "../components/addBillModal/AddOfferModal"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import FloatingActionButton from "../components/FloatingActionButton"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { Authorized } from "../../../components/authentication/Authorized"

export default function OfferPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.OFFER_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm, LocalStorageKeys.OFFER_FILTER + "searchTerm")
    const [filterStatus, setfilterStatus] = useState(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "status"))
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "dateSince"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "dateTo"))
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "archived"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.OFFER_FILTER + "invertSort"))

    useResetPageOnFilterChange([searchParams, filterStatus, filterDateSince, filterDateTo, filterArchived, invertSort], () => handlePageChange(1))
    
    // Get Data Query
    const queryKey = GeneralQueryKeys.OFFER_DATA + page + searchParams + filterStatus + filterDateSince + filterDateTo + filterArchived + invertSort
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "offer",
                page: page,
                limit: 15,
                search: searchParams,
                status: filterStatus,
                startDate: filterDateSince,
                endDate: filterDateTo,
                archived: filterArchived,
                invertSort: invertSort,
            }),
    })

    const modalCon = useContext(ModalContext)
    const [showFilter, setshowFilter] = useState(false)

    const handleRemoveFilter = () => {
        setfilterStatus("")
        setfilterDateTo("")
        setfilterDateSince("")
        setfilterArchived(false)
        setinvertSort(undefined)
    }

    const displayedOffers = useMemo(() => data?.docs, [data?.docs])

    const offerTableColumns: ColumnType[] = [
        {
            label: "Datum",
            responsive: "hidden sm:table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: window.innerWidth > 640 ? "Angebotsnummer" : "Nr.",
            width: "w-fit min-w-[8rem]",
        },
        {
            label: "Betrag",
            width: "w-fit min-w-[5rem] ",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Kunde",
            responsive: "hidden md:table-cell ",
        },
        {
            label: "Objekt",
            responsive: "hidden lg:table-cell ",
        },
        {
            label: "Status",
            responsive: "table-cell",
            width: "min-w-[10rem] w-fit",
        },
    ]
    return (
        <BasePage>
            <BaseHeadBanner
                title={"Angebote"}
                button={
                    <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
                        <PLUSINCIRCLE className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(1)} />
                    </Authorized>
                }
            />
            {/* Button Row */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Kunde / Objekt suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !isLoading && !error && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps="overflow-x-hidden my-4">
                        <BaseTable
                            columns={offerTableColumns}
                            tbody={displayedOffers?.map((item: OfferGet) => (
                                <OfferTableRow
                                    key={item._id}
                                    id={item._id}
                                    title={"Angebot " + item.nr}
                                    amount={formatCurrency(
                                        item.positions.reduce((acc: any, curr: any) => {
                                            return acc + curr.singlePrice * curr.amount
                                        }, 0)
                                    ).toString()}
                                    customer={item?.customer_id?.customer}
                                    object={item?.object_id}
                                    status={item.status}
                                    date={item.date}
                                    archived={item.archived}
                                />
                            ))}
                        />
                        {(!displayedOffers || displayedOffers.length === 0) && (
                            <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erstellten Angebote</p>
                        )}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        setVisible={() => setshowFilter(!showFilter)}
                        searchdateSince={filterDateSince}
                        changeDateSince={setfilterDateSince}
                        searchdateTo={filterDateTo}
                        changeDateTo={setfilterDateTo}
                        searchStatus={filterStatus}
                        changeStatus={setfilterStatus}
                        statusType={"offer"}
                        removeFilter={handleRemoveFilter}
                        searchArchived={filterArchived}
                        changeArchived={setfilterArchived}
                        sessionStorageKey={LocalStorageKeys.OFFER_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            defaultCurrent={1}
                            pageSize={15}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </>
            )}
            <FloatingActionButton currentPage="offer" />
            <AddOfferModal queryKey={queryKey} />
        </BasePage>
    )
}
